import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

export type IPaymentReceipts = IPaymentReceipt[];

export interface IPaymentReceipt {
  receipt_id: string;
  user_id: number;
  body: IReceiptBody;
  updated_at: string;
  details: IReceiptDetails;
  status: string;
  operation_type: string;
}

export interface IReceiptBody {
  items: IReceiptItem[];
  payments: IReceiptPayment[];
}

export interface IReceiptItem {
  art_id: number;
  name: string;
  price: number;
  quantity: number;
  payment_method: string;
  payment_object: string;
  sum: number;
  vat: IReceiptVat;
}

export interface IReceiptVat {
  type: string;
}

export interface IReceiptPayment {
  type: number;
  sum: number;
}

export interface IReceiptDetails {
  total: number;
  fns_site: string;
  fn_number: string;
  shift_number: number;
  receipt_datetime: string;
  fiscal_receipt_number: number;
  fiscal_document_number: number;
  ecr_registration_number: string;
  fiscal_document_attribute: number;
  ofd_inn: number;
  ofd_receipt_url: string;
}

interface IPaymentReceiptParams {
  // money movements
  mm: string;
}

interface IPaymentReceiptsState {
  receipts: IPaymentReceipts;
  actions: {
    getPaymentReceipts: IReduxRequestNode<
      IPaymentReceiptParams,
      IPaymentReceipts
    >;
  };
}

const initialState: IPaymentReceiptsState = {
  receipts: [],
  actions: {
    getPaymentReceipts: {},
  },
};

export const getPaymentReceipts = createAsyncThunk(
  "pages/paymentReceipts/getPaymentReceipts",
  async (params: IPaymentReceiptParams) => {
    const response = new Request.Cat3<IPaymentReceiptParams, IPaymentReceipts>({
      url: "/receipt/",
      provider: {
        id: "GET_PAYMENT_RECEIPTS",
      },
      request: {
        params: params,
      },
    });

    return await response.fetch();
  }
);

export const paymentReceiptsSlice = createSlice({
  name: "pages/paymentReceipts",
  initialState,
  reducers: {
    setPaymentReceipts: (
      state: IPaymentReceiptsState,
      action: PayloadAction<IPaymentReceipts>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.paymentReceipts");
    // Загрузка
    builder.addCase(
      getPaymentReceipts.pending,
      (state: IPaymentReceiptsState) => {
        state.actions.getPaymentReceipts.isLoading = true;
        state.actions.getPaymentReceipts.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      getPaymentReceipts.fulfilled,
      (
        state: IPaymentReceiptsState,
        action: PayloadAction<
          IReduxRequestNode<IPaymentReceiptParams, IPaymentReceipts>
        >
      ) => {
        state.actions.getPaymentReceipts.isLoading = false;

        state.receipts = action.payload.response || [];

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса getPaymentReceipts",
            action.payload.error
          );
          state.actions.getPaymentReceipts.error = action.payload.error;
        }
      }
    );
  },
});

export const { setPaymentReceipts } = paymentReceiptsSlice.actions;

export default paymentReceiptsSlice.reducer;
